import pb, { requireAuth } from '../pocketbaseClient';

/**
 * The parts of a job's export that neither the CMS nor the assessment keeps.
 *
 * Two kinds live here. The Indeed and AfA texts, which HR pastes into the
 * portals by hand and edits in the dashboard. And the originals the download
 * needs to hand the export back: the base name, the Google Jobs JSON (the CMS
 * maps it onto fields and drops the file), the storyboard, and the invitation
 * mail as uploaded, whose header the assessment does not keep.
 *
 * Keyed by the CMS job id, for the same reason as the assessments: the file
 * name is rewritten by the CMS on collisions, the id is not.
 */

const COLLECTION = 'job_documents';

const NOT_FOUND = 404;

const MISSING_COLLECTION_HINT =
    `Die PocketBase-Collection "${COLLECTION}" existiert nicht. ` +
    'Bitte im PocketBase-Admin anlegen (siehe docs/stellen-dateien.md).';

function describeError(error, fallback) {
    if (error?.status === NOT_FOUND) return MISSING_COLLECTION_HINT;
    return error?.message ? `${fallback}: ${error.message}` : fallback;
}

function normalize(record) {
    if (!record) return null;
    return {
        id: record.id,
        cmsJobId: record.cms_job_id,
        label: record.label || '',
        baseName: record.base_name || '',
        googleJobsJson: record.google_jobs_json || '',
        storyboardMd: record.storyboard_md || '',
        emailMarkdown: record.email_markdown || '',
        indeedText: record.indeed_text || '',
        afaText: record.afa_text || '',
        updatedAt: record.updated || null,
    };
}

/** Service field name → PocketBase column. */
const COLUMNS = {
    label: 'label',
    baseName: 'base_name',
    googleJobsJson: 'google_jobs_json',
    storyboardMd: 'storyboard_md',
    emailMarkdown: 'email_markdown',
    indeedText: 'indeed_text',
    afaText: 'afa_text',
};

class JobDocumentService {
    /** The documents attached to one CMS job, or null. */
    async getByJobId(jobId) {
        try {
            requireAuth();
            // getList, not getFirstListItem: see AssessmentService.getByJobId.
            const page = await pb
                .collection(COLLECTION)
                .getList(1, 1, { filter: pb.filter('cms_job_id = {:id}', { id: String(jobId) }) });
            return { success: true, data: normalize(page.items[0]) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Dokumente konnten nicht geladen werden') };
        }
    }

    /** Every record in full, for the download of all jobs. */
    async getAll() {
        try {
            requireAuth();
            const records = await pb.collection(COLLECTION).getFullList();
            return { success: true, data: records.map(normalize) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Dokumente konnten nicht geladen werden'), data: [] };
        }
    }

    /**
     * Which channels each job has a text for, as a Map of job id → { indeed, afa }.
     *
     * Asks for a one-character excerpt instead of the texts themselves: the
     * cards only need to know whether one is there, not what it says.
     */
    async getChannelsByJobId() {
        try {
            requireAuth();
            const records = await pb.collection(COLLECTION).getFullList({
                fields: 'cms_job_id,indeed_text:excerpt(1),afa_text:excerpt(1)',
            });
            return {
                success: true,
                data: new Map(
                    records.map((r) => [
                        String(r.cms_job_id),
                        { indeed: Boolean(r.indeed_text), afa: Boolean(r.afa_text) },
                    ])
                ),
            };
        } catch (error) {
            // Only feeds the card hints, so a missing collection must not take
            // the page down.
            return { success: false, error: describeError(error, 'Texte konnten nicht geladen werden'), data: new Map() };
        }
    }

    /**
     * Create or update the documents of a job.
     *
     * A field passed as undefined is left as it is. A ZIP that carries only the
     * Indeed file must not wipe an AfA text someone wrote by hand.
     */
    async save({ jobId, existingId, ...fields }) {
        try {
            requireAuth();

            const data = { cms_job_id: String(jobId) };
            for (const [key, column] of Object.entries(COLUMNS)) {
                if (fields[key] !== undefined) data[column] = fields[key];
            }

            const record = existingId
                ? await pb.collection(COLLECTION).update(existingId, data)
                : await pb.collection(COLLECTION).create(data);

            return { success: true, data: normalize(record) };
        } catch (error) {
            return { success: false, error: describeError(error, 'Speichern fehlgeschlagen') };
        }
    }
}

export const jobDocumentService = new JobDocumentService();
export default jobDocumentService;
